import {
  LaunchTask,
  WorkStatus,
  ResolveLaunchTaskCommand,
  ResolveLaunchTaskCommandInput,
  ResolveLaunchTaskCommandOutput,
  GetEntityBuildRequestInfoCommand,
  Group,
  GroupSummary,
  ListGroupsCommand,
  GetGroupCommand,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  ExpandableSection,
  Header,
  Link,
  Popover,
  SpaceBetween,
  Spinner,
  TextContent,
} from '@amzn/awsui-components-console';
import { AuditLog } from './AuditLog';
import { BuddyLaunches } from './BuddyLaunches';
import { WriterSection } from './writerSection';
import { DetailSection } from './conferenceDetailSection';
import { Packages } from './PackagesEditor/Packages';
import { convertToPT } from '../utils';
import { useHistory, useLocation } from 'react-router-dom';
import { callApi } from '../../../client';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useAuthState } from '../../../authentication/context';
import { WorkStatusBadge } from './WorkStatusBadge';
import { AddTasksToGroupModal } from '../../../GroupPages/AddTasksToGroupModal';
import { CreateGroupModal } from '../../../GroupPages/CreateGroupModal';
import { BuildIdsMapComponent } from './BuildIdsMapComponent';
import { PreviewGuidance } from './PreviewGuidance';
//import { AssignedReaders } from '../ui/readerSection';
import AdvanceErrorHandlingPage from '../../AdvancedErrorPage';
import { AskFusionAssistantButton } from '../../../fusionAssistant/AskFusionAssistantButton';

interface LaunchTaskProps {
  launchTask?: LaunchTask;
  id: string;
  detailLoading: boolean;
  detailFetchStatus: number;
  errorMsg: string;
  buddyLaunchesLoading: boolean;
  buddyLaunches: LaunchTask[];
  buddyLaunchFetchSuccess: boolean;
  auditLog?: LaunchTask['auditLog'];
}

export const TaskDetail = ({
  id,
  launchTask,
  detailLoading,
  detailFetchStatus,
  errorMsg,
  buddyLaunches,
  buddyLaunchesLoading,
  buddyLaunchFetchSuccess,
  auditLog,
}: LaunchTaskProps) => {
  const [children, setChildren] = useState(0);
  const { token } = useAuthState();
  const [descriptionText, setDescriptionText] = useState('');

  const [packages, setPackages] = useState(
    launchTask?.packageBuildInfoList ?? []
  );

  const currentLocation = useLocation();
  const history = useHistory();

  const [buddyLaunchesList, setBuddyLaunchesList] = useState(buddyLaunches);

  const [approved, setApproved] = useState(false);
  const [manifestBuilt, setManifestBuilt] = useState(false);
  const [publishScheduled, setPublishScheduled] = useState(false);

  const [publish, setPublish] = useState(false);

  const [workStatus, setWorkStatus] = useState(launchTask?.workStatus);

  const isGrouped = launchTask?.workStatus === WorkStatus.GROUPED;
  const isTerminalTask =
    launchTask?.workStatus === WorkStatus.DELETED ||
    launchTask?.workStatus === WorkStatus.SHIPPED ||
    launchTask?.workStatus === WorkStatus.RESOLVED;

  const [resolvedGroup, setResolvedGroup] = useState<Group | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [addToGroupModalVisible, setAddToGroupModalVisible] = useState(false);
  const [createGroupModalVisible, setCreateGroupModalVisible] = useState(false);

  useEffect(() => {
    if (isGrouped && token && launchTask) {
      void resolveGroup();
    }
  }, [isGrouped, token, launchTask?.launchId]);

  async function resolveGroup() {
    if (!token || !launchTask) return;
    setResolvedGroup(null);
    setResolveLoading(true);
    setResolveError(null);
    try {
      const listResponse = await callApi(
        new ListGroupsCommand({
          conference: launchTask.conference,
          venue: launchTask.venue,
        }),
        token
      );
      const groups: GroupSummary[] = listResponse.groups ?? [];
      const match = groups.find((g) =>
        g.memberTaskIds?.includes(launchTask.launchId)
      );
      if (match) {
        const getResponse = await callApi(
          new GetGroupCommand({ groupId: match.groupId }),
          token
        );
        if (getResponse.group) {
          setResolvedGroup(getResponse.group);
        } else {
          setResolveError('No matching group found for this task.');
        }
      } else {
        setResolveError('No matching group found for this task.');
      }
    } catch (err: any) {
      console.error('Failed to resolve group for task:', err);
      setResolveError('Failed to load group information.');
    } finally {
      setResolveLoading(false);
    }
  }

  useEffect(() => {
    setWorkStatus(launchTask?.workStatus);
  }, [launchTask?.workStatus]);

  useEffect(() => {
    setManifestBuilt(workStatus === WorkStatus.MANIFEST_BUILT);
    setApproved(workStatus === WorkStatus.REVIEW_APPROVED);
    setPublishScheduled(workStatus === WorkStatus.PUBLISH_SCHEDULED);
  }, [workStatus]);

  function removeDuplicates(arr: LaunchTask[]) {
    const uniqueTasks = new Map<string, LaunchTask>();
    arr.forEach((task) => {
      uniqueTasks.set(task.launchId, task);
    });
    return Array.from(uniqueTasks.values());
  }

  useEffect(() => {
    setBuddyLaunchesList(removeDuplicates(buddyLaunches));
    if (launchTask) {
      if (launchTask.packageBuildInfoList) {
        setPackages(launchTask.packageBuildInfoList);
      }
    }

    if (launchTask && launchTask.childTask) {
      setChildren(launchTask.childTask.length);
    }

    let description = '';

    description += 'Last Modified: ';
    if (launchTask?.modifiedAt) {
      description += convertToPT(launchTask?.modifiedAt, true);
    }

    description += '.   Last Modified by: ';
    if (launchTask?.modifiedBy) {
      description += launchTask?.modifiedBy + '.   ';
    }

    setDescriptionText(description);
  }, [launchTask, buddyLaunches, location.pathname]);

  if (detailLoading) {
    return <Spinner size='large' />;
  }

  if (Math.floor(detailFetchStatus / 100) !== 2 || !launchTask) {
    return (
      <AdvanceErrorHandlingPage
        status={detailFetchStatus}
        errorMsg={errorMsg}
      />
    );
  }

  async function ResolveTaskApi(input: ResolveLaunchTaskCommandInput) {
    if (!token) throw new NoTokenError();

    try {
      const response: ResolveLaunchTaskCommandOutput = await callApi(
        new ResolveLaunchTaskCommand(input),
        token
      );

      if (response.$metadata.httpStatusCode == 200) {
        // intentionally empty
      } else {
        console.error(
          'Failed to resolve task, status code:',
          response.$metadata.httpStatusCode
        );
      }
    } catch (err: any) {
      console.log('error with ResolveTaskApi', err);
    }
  }

  return (
    <Box>
      <Container
        header={
          <Header
            actions={
              <SpaceBetween
                direction='horizontal'
                size='l'
                alignItems={'center'}
              >
                {launchTask?.workStatus ? (
                  <WorkStatusBadge status={launchTask.workStatus} />
                ) : (
                  <Badge color='grey'>Status: -</Badge>
                )}
                {launchTask?.workStatus === WorkStatus.PUBLISH_SCHEDULED &&
                launchTask?.allowedActions?.includes('WRITE') &&
                launchTask?.targetDocLiveTime instanceof Date &&
                launchTask.targetDocLiveTime < new Date() ? (
                  <Button
                    onClick={() => {
                      void ResolveTaskApi({
                        launchId: launchTask.launchId,
                      }).then(() => {
                        location.reload();
                      });
                    }}
                  >
                    Resolve task
                  </Button>
                ) : null}
                <Popover
                  content='Submitting initial content builds by the Due date ensures there is enough time to address issues before the Final build deadline (ELTB). You can continue to make changes until the Final build deadline (ELTB).'
                  position='bottom'
                  size='small'
                  triggerType='text'
                >
                  <TextContent>
                    <p>
                      <strong>
                        Due date:{' '}
                        {launchTask?.dueDate
                          ? convertToPT(launchTask?.dueDate, false)
                          : '-'}
                      </strong>
                    </p>
                  </TextContent>
                </Popover>
                {!isGrouped && !isTerminalTask && (
                  <Button
                    onClick={() => setAddToGroupModalVisible(true)}
                    data-test-id='add-to-group-button'
                  >
                    Add to group
                  </Button>
                )}
                {launchTask && (
                  <AskFusionAssistantButton
                    dataTestId='ask-fusion-assistant-task-button'
                    context={{
                      launchId: launchTask.launchId,
                      taskName: launchTask.name,
                      groupName: resolvedGroup?.groupName,
                      packages: (launchTask.packageBuildInfoList ?? []).map(
                        (pkg) => ({
                          packageName: pkg.packageName ?? '',
                          commitId: pkg.commitId,
                        })
                      ),
                    }}
                  />
                )}
              </SpaceBetween>
            }
            description={
              <SpaceBetween direction='vertical' size='xxs'>
                {launchTask.parentTask && (
                  <span>
                    <strong>Parent task: </strong>
                    <Link href={`#/conferences/view-${launchTask.parentTask}`}>
                      {launchTask.parentTask}
                    </Link>{' '}
                    <Badge color='blue'>This is a buddy launch task</Badge>
                  </span>
                )}
                <span>{descriptionText}</span>
              </SpaceBetween>
            }
          >
            Launch task detail
          </Header>
        }
      >
        <SpaceBetween direction='vertical' size='l'>
          <DetailSection
            id={id}
            loading={detailLoading}
            launchTask={launchTask}
          />

          <WriterSection task={launchTask} />

          {/* Show group info panel for GROUPED tasks, packages section otherwise */}
          {isGrouped ? (
            <ExpandableSection
              variant='container'
              defaultExpanded={true}
              headerText='Group membership'
            >
              <SpaceBetween size='m'>
                <Box>
                  This task belongs to a group. Doc packages are managed at the
                  group level.
                </Box>
                {resolveLoading ? (
                  <Box>Loading group information...</Box>
                ) : resolveError ? (
                  <Box color='text-status-error'>{resolveError}</Box>
                ) : resolvedGroup ? (
                  <SpaceBetween size='s'>
                    <div>
                      <Box variant='awsui-key-label'>Group</Box>
                      <Link href={`#/groups/view-${resolvedGroup.groupId}`}>
                        {resolvedGroup.groupName}
                      </Link>
                    </div>
                    <div>
                      <Box variant='awsui-key-label'>Group status</Box>
                      <WorkStatusBadge status={resolvedGroup.workStatus} />
                    </div>
                    <BuildIdsMapComponent
                      buildsMap={resolvedGroup.buildIdsMap ?? null}
                    />
                    <Button
                      variant='primary'
                      href={`#/groups/view-${resolvedGroup.groupId}`}
                    >
                      View group
                    </Button>
                  </SpaceBetween>
                ) : (
                  <Box>Loading group information...</Box>
                )}
              </SpaceBetween>
            </ExpandableSection>
          ) : (
            <ExpandableSection
              variant='container'
              defaultExpanded={true}
              headerText={`Doc packages (${
                launchTask.packageBuildInfoList?.length ?? 0
              })`}
              headerActions={
                <SpaceBetween
                  size={'m'}
                  direction={'horizontal'}
                  alignItems={'center'}
                >
                  {(approved || manifestBuilt || publishScheduled) && (
                    <SpaceBetween
                      size={'xs'}
                      direction={'horizontal'}
                      alignItems={'center'}
                    >
                      <Button
                        target='_blank'
                        variant='normal'
                        href={launchTask.reviewEndpoint}
                      >
                        Preview
                      </Button>
                      <PreviewGuidance />
                    </SpaceBetween>
                  )}
                  {(approved || manifestBuilt) && (
                    <Button
                      variant='normal'
                      onClick={() => setPublish(true)}
                      disabled={approved}
                    >
                      Ready to Publish
                    </Button>
                  )}
                  {launchTask.workStatus !== WorkStatus.DELETED && (
                    <Button
                      variant='primary'
                      onClick={() =>
                        (location.href = `/#/conferences/view-${launchTask.launchId.toString()}/packages`)
                      }
                    >
                      Edit
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              <Box display={packages.length ? 'block' : 'none'}>
                <Packages
                  task={launchTask}
                  onPoll={setWorkStatus}
                  publish={publish}
                  onPublishing={() => setPublish(false)}
                  onPollStatus={async () => {
                    if (!token) throw new NoTokenError();
                    return callApi(
                      new GetEntityBuildRequestInfoCommand({
                        entityId: launchTask.launchId,
                        entityType: 'TASK',
                      }),
                      token
                    );
                  }}
                />
              </Box>
            </ExpandableSection>
          )}

          <Box display={launchTask.parentTask ? 'none' : 'block'}>
            <ExpandableSection
              variant='container'
              defaultExpanded={true}
              headerText={`Buddy launch information (${
                buddyLaunchesList.length ?? 'loading...'
              })`}
              headerDescription='Use this section to add launch ID for a buddy launch. A buddy launch item for another service to support your launch. For API Reference documentation, please provide all package dependencies.'
              headerActions={
                <Button
                  variant='primary'
                  disabled={launchTask.workStatus === WorkStatus.DELETED}
                  href={`#${currentLocation.pathname}/buddylaunches`}
                >
                  Edit
                </Button>
              }
            >
              <Box display={buddyLaunchesList.length > 0 ? 'block' : 'none'}>
                {buddyLaunchesList && (
                  <BuddyLaunches
                    launchTaskDetail={launchTask}
                    buddyLaunches={buddyLaunchesList}
                    onCountChange={setChildren}
                    buddyLaunchesLoading={buddyLaunchesLoading}
                    buddyLaunchFetchSuccess={buddyLaunchFetchSuccess}
                  />
                )}
              </Box>
            </ExpandableSection>
          </Box>
          <Box display='block'>
            <ExpandableSection
              variant='container'
              defaultExpanded={false}
              headerText='Audit Log'
              headerDescription='Displays recent changes to this FLC task (up to 10).'
            >
              <AuditLog auditLog={auditLog} />
            </ExpandableSection>
          </Box>
        </SpaceBetween>
      </Container>

      {addToGroupModalVisible && launchTask && (
        <AddTasksToGroupModal
          visible={addToGroupModalVisible}
          conference={launchTask.conference ?? ''}
          venue={launchTask.venue ?? ''}
          preSelectedTaskIds={[launchTask.launchId]}
          onDismiss={() => setAddToGroupModalVisible(false)}
          onSuccess={(groupId) => {
            setAddToGroupModalVisible(false);
            history.push(`/groups/view-${groupId}`);
          }}
          onSwitchToCreate={() => {
            setAddToGroupModalVisible(false);
            setCreateGroupModalVisible(true);
          }}
        />
      )}

      {createGroupModalVisible && (
        <CreateGroupModal
          visible={createGroupModalVisible}
          onDismiss={() => setCreateGroupModalVisible(false)}
          onSuccess={(groupId) => {
            setCreateGroupModalVisible(false);
            history.push(`/groups/view-${groupId}`);
          }}
        />
      )}
    </Box>
  );
};

export default TaskDetail;
